import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Languages, Calendar, MessageCircle, Phone, CheckCircle2, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1823" data-mp="s69l1nhz8l5o">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1824" data-mp="o5fyoyk6neew" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1825" data-mp="qvxzvic7l0am">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1826" data-mp="quekrv2agvaf">
        <span className="text-lg font-medium text-gray-900 group-hover:text-forest-700 transition-colors" data-id="element-1827" data-mp="3ucxyzto4u0m8">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-forest-600 text-white' : 'bg-stone-100 text-gray-400'}`} data-id="element-1828" data-mp="34skf4a6ka0u">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1829" data-mp="11gd98jt1b6uj" /> : <Plus className="w-4 h-4" data-id="element-1830" data-mp="1f8b35ne6cw64" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1831" data-mp="1b7r6sm1fdwl1">
        <p className="text-gray-600 leading-relaxed" data-id="element-1832" data-mp="nk9k34x0poox">{answer}</p>
      </div>
    </div>;
}
export function EmergencyDentistLondonRuPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Что делать, если сильно болит зуб?',
    a: 'Как можно скорее свяжитесь с нами для неотложного осмотра. Мы определим причину боли и предложим лечение. Не грейте больное место!'
  }, {
    q: 'Принимаете ли вы срочно в тот же день?',
    a: 'Да, мы всегда стараемся найти время для пациентов с острой болью в тот же день. Напишите в WhatsApp/Telegram или позвоните.'
  }, {
    q: 'Сколько стоит экстренный прием?',
    a: 'Стоимость зависит от необходимых процедур и диагностики. Экстренная консультация — £60. После осмотра мы объясним варианты лечения и цены.'
  }, {
    q: 'Какие симптомы считаются неотложными?',
    a: 'Сильная зубная боль, отек лица или десен, гной, повышенная температура, боль при глотании, травма зуба (скол, вывих), кровотечение после удаления.'
  }, {
    q: 'Что делать, если выбили зуб?',
    a: 'Найдите зуб, держите его за коронку (не за корень!), промойте водой, если грязный. Попробуйте вставить обратно в лунку или положите в молоко/слюну. Обратитесь к врачу в течение 30-60 минут!'
  }, {
    q: 'Работаете ли вы в выходные?',
    a: 'Мы работаем с понедельника по субботу. В неотложных случаях напишите нам в WhatsApp, мы попробуем помочь или посоветовать.'
  }, {
    q: 'Можно ли получить помощь на русском?',
    a: 'Да, в Dentico Clinic возможно общение на русском языке. Вы сможете точно описать свои симптомы.'
  }, {
    q: 'Где вы находитесь?',
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington), рядом с Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1833" data-mp="3d75ph8ulnvpr">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1834" data-mp="3d6w644428mt1" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1835" data-mp="3h6527fmkowrr" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1836" data-mp="3qxr94zrvo9op" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1837" data-mp="4rlyyhlbcree">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1838" data-mp="4ozs6k68yfx8" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1839" data-mp="25t210a6jlm7p">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1840" data-mp="20u7hf60r3tmj">
            <div className="space-y-6" data-id="element-1841" data-mp="20rura5kuacye">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1842" data-mp="2elvupcmqko53">
                <AlertCircle className="w-4 h-4" data-id="element-1843" data-mp="29tcfyccbbmyp" />
                Неотложная помощь
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1844" data-mp="2ohp4io92m3j6">
                Неотложная стоматологическая помощь в Лондоне –{' '}
                <span className="text-red-600" data-id="element-1845" data-mp="2xjolxhnzz9mn">Dentico Clinic</span>
                <br data-id="element-1846" data-mp="2tid2jtvpvkmw" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1847" data-mp="2tjxxvlpuv7lt">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1848" data-mp="z53tu4azw35p">
                Острая боль? Сломанный зуб? Отек? Мы принимаем пациентов с
                острой болью в тот же день. Консультация на русском языке.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-[#2F4A3F]" data-id="element-1849" data-mp="fb3rlvalga2a">
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1850" data-mp="k9ykjq29zf04">
                  <Clock className="w-4 h-4 text-[#2F4A3F]" data-id="element-1851" data-mp="5cejgu8i23z8" />
                  Срочный приём
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1852" data-mp="actyrrccdlju">
                  <MapPin className="w-4 h-4 text-[#2F4A3F]" data-id="element-1853" data-mp="3sjgmh9bfskp7" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-[#F8F5F0] px-3 py-1.5 rounded-lg border border-[#D8C4A8] shadow-sm" data-id="element-1854" data-mp="1cxbnu5louqw">
                  <Languages className="w-4 h-4 text-[#2F4A3F]" data-id="element-1855" data-mp="1vpn56as241xr" />
                  Русский язык
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1856" data-mp="34917rp3q2ty2">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1857" data-mp="2za6fzxns1v1t">
                  <Button variant="whatsapp" size="lg" className="w-full gap-2" data-id="element-1858" data-mp="2qh33brkyhm88">
                    <MessageCircle className="w-5 h-5" data-id="element-1859" data-mp="2ljks1ujguux2" />
                    Написать в WhatsApp
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1860" data-mp="2gidltzwz23si">
                  <Button variant="secondary" size="lg" className="w-full gap-2" data-id="element-1861" data-mp="2blwwx3hy7led">
                    <Phone className="w-5 h-5" data-id="element-1862" data-mp="9tif04femgxy" />
                    +44 7345 197820
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1863" data-mp="5sorvd9sd664">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1864" data-mp="1nps4y6u7zftl"></div>
              <img src="https://images.unsplash.com/photo-1598256989800-fe5f95da9787?auto=format&fit=crop&w=1200&q=80" alt="Неотложная стоматология Лондон" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1865" data-mp="1sntostzgzj64" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1866" data-mp="19qanydn616us">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1867" data-mp="1drchcdueioou">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1868" data-mp="1dq1c09lb58vm">
            Когда нужна неотложная помощь?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1869" data-mp="2p6rlq2oxwitz">
            {[{
            title: 'Сильная зубная боль',
            desc: 'Боль, которая не проходит от обезболивающих, пульсирующая или ночная боль.'
          }, {
            title: 'Отек лица',
            desc: 'Припухлость щеки или десны может свидетельствовать о серьезной инфекции.'
          }, {
            title: 'Травма зуба',
            desc: 'Сломанный, сколотый или выбитый зуб в результате удара или падения.'
          }, {
            title: 'Потеря пломбы/коронки',
            desc: 'Если выпала пломба или коронка, и зуб стал чувствительным или острым.'
          }, {
            title: 'Кровотечение',
            desc: 'Кровотечение из десны после удаления зуба, которое не останавливается.'
          }, {
            title: 'Боль при глотании',
            desc: 'Затрудненное глотание или дыхание — это признак опасного распространения инфекции.'
          }].map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1870" data-mp="1lia748tlrnh8" data-mpi={idx}>
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1871" data-mp="360ufvnw8dio3">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1872" data-mp="321lnongu9sun" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1873" data-mp="2x89cbf167iju">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1874" data-mp="2nbwjejdg98q5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1875" data-mp="2ncxw0sa4639z">
          <div className="text-center mb-10" data-id="element-1876" data-mp="2ibi7gq8q9emu">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1877" data-mp="2iewdc8glo5z1">
              Что делать до визита к врачу?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1878" data-mp="29cepjplar59x">
              Советы первой помощи, пока вы ждете приема.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1879" data-mp="cj33plgr8wpc">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1880" data-mp="7kifkkes23h9">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1881" data-mp="7l1lf2r5l1ex">
                При зубной боли
              </h3>
              <p className="text-gray-600" data-id="element-1882" data-mp="qn0qlcpb7j0f">
                Примите обезболивающее (ибупрофен или парацетамол), прополощите
                рот теплой водой. Не прикладывайте аспирин к десне и не грейте
                больное место!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1883" data-mp="mobgpu3hcjlj">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1884" data-mp="1ackxghlu6h5t">
                Если выбили зуб
              </h3>
              <p className="text-gray-600" data-id="element-1885" data-mp="1edoakwci1z3c">
                Держите зуб только за коронку (белую часть). Если грязный —
                промойте молоком или водой. Попробуйте вставить обратно в лунку
                или положите в молоко/слюну. Время критично — у вас есть 30-60
                минут!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1886" data-mp="3l78llucm8stt">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1887" data-mp="3q5a0s46x8800">
                При отеке
              </h3>
              <p className="text-gray-600" data-id="element-1888" data-mp="3ij68woyyoyk6">
                Приложите холодный компресс к щеке снаружи. Это уменьшит отек и
                боль. Не используйте тепло!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1889" data-mp="3mm2u8nfvz8rt">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1890" data-mp="3mk8ahcgcpmpb">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1891" data-mp="2yxjp2gs4vc3y">
            Как мы работаем в экстренных случаях
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1892" data-mp="2utbwg8ik1c0g">
            {[{
            step: '01',
            title: 'Связь',
            desc: 'Напишите нам в WhatsApp или позвоните. Опишите симптомы.'
          }, {
            step: '02',
            title: 'Оценка',
            desc: 'Мы оценим срочность и найдем ближайшее время для визита.'
          }, {
            step: '03',
            title: 'Визит',
            desc: 'Осмотр, рентген-диагностика и снятие острой боли.'
          }, {
            step: '04',
            title: 'План',
            desc: 'Если требуется дальнейшее лечение, мы составим план действий.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1893" data-mp="33kltf5b9frvn" data-mpi={idx}>
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1894" data-mp="37k4h28uguh26">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1895" data-mp="3hi0mql4mwjsw">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1896" data-mp="3hjbwd1krglrx">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1897" data-mp="1n4j1j4rpbz11">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1898" data-mp="98hogu90e5gl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1899" data-mp="99axy2zcr92f">
          <div className="text-center mb-12" data-id="element-1900" data-mp="4i16qxy5nxof">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1901" data-mp="4g83lsmr3vl6">
              Виды неотложной помощи
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1902" data-mp="3rmh5pndeuh0x">
            {[{
            title: 'Снятие острой боли',
            desc: 'Медикаментозная обработка и временная пломба.'
          }, {
            title: 'Удаление зуба',
            desc: 'Если зуб невозможно спасти или он является источником инфекции.'
          }, {
            title: 'Лечение каналов',
            desc: 'Удаление воспаленного нерва (пульпэктомия) для спасения зуба.'
          }, {
            title: 'Фиксация коронки',
            desc: 'Цементирование выпавшей коронки или моста.'
          }, {
            title: 'Вскрытие абсцесса',
            desc: 'Дренаж гноя для снятия отека и боли.'
          }, {
            title: 'Шинирование зубов',
            desc: 'Фиксация подвижных зубов после травмы.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1903" data-mp="2wcvp6lhhgpmc" data-mpi={idx}>
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1904" data-mp="2wfhwql4dj54k" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1905" data-mp="2hng1jed910av">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1906" data-mp="2mgscilp098l5">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1907" data-mp="2cowf7cuqtmhz">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1908" data-mp="2cpnz3qez9vsf">
          <div className="text-center mb-10" data-id="element-1909" data-mp="1ypd9srtbgp9d">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1910" data-mp="1yrpw1h63xls8">
              Частые вопросы
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1911" data-mp="1k2o7yjbldzzt">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1912" data-mp="1k4r83w6gl9i9" data-mpi={idx} />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1913" data-mp="231iqm3e6fkl9">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1914" data-mp="22zpew9opia6e">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1915" data-mp="1y1xqvqr6n87h">
            Смотрите также
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1916" data-mp="2cr2937xz9cfl">
            <Link to="/ru/russkij-stomatolog-london" data-id="element-1917" data-mp="27sscqu0io5se">
              <Button variant="outline" size="sm" data-id="element-1918" data-mp="27t0naf962z0f">
                Русскоязычный стоматолог
              </Button>
            </Link>
            <Link to="/ru/lechenie-kanalov-london" data-id="element-1919" data-mp="2hqompihpwuox">
              <Button variant="outline" size="sm" data-id="element-1920" data-mp="2hrzm7644rga8">
                Лечение каналов
              </Button>
            </Link>
            <Link to="/ru/services/extraction" data-id="element-1921" data-mp="3o7qpmmxoyviq">
              <Button variant="outline" size="sm" data-id="element-1922" data-mp="3t3xxwmdk8j3u">
                Удаление зубов
              </Button>
            </Link>
            <Link to="/ru/prices" data-id="element-1923" data-mp="1zr3hfxu2vgh">
              <Button variant="outline" size="sm" data-id="element-1924" data-mp="1wles1t49vla">
                Цены
              </Button>
            </Link>
            <Link to="/ru/contact" data-id="element-1925" data-mp="35f500kddo16l">
              <Button variant="outline" size="sm" data-id="element-1926" data-mp="35g7ludvufdfn">
                Контакты
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1927" data-mp="3efkvp7nvq40w">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1928" data-mp="3edqdi13zsq0i">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1929" data-mp="3j7l29iygfv6g">
            Нужна срочная помощь?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1930" data-mp="2moet8kkoru8g">
            Не ждите, пока боль усилится. Свяжитесь с нами прямо сейчас.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1931" data-mp="2j7968b0e0kjg">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1932" data-mp="2e7mu64wapac8">
              <Button variant="whatsappInverse" size="lg" className="w-full gap-2" data-id="element-1933" data-mp="2a85obacyib6j">
                <MessageCircle className="w-5 h-5" data-id="element-1934" data-mp="208529gnpdgyn" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1935" data-mp="1vg3ry0a3i0b9">
              <Button variant="outlineInverse" size="lg" className="w-full gap-2" data-id="element-1936" data-mp="1qhk6s23bssjy">
                <Phone className="w-5 h-5" data-id="element-1937" data-mp="1mi1jx0ry27eh" />
                +44 7345 197820
              </Button>
            </a>
          </div>
        </div>
      </section>
    </Layout>;
}